import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGoogle,
  faInstagram,
  faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import NavLinks from './NavLinks';
import logoImage from '../../images/logoRM.webp';

const googleMapsUrl =
  'https://www.google.com/maps/place/Amonthanat+Massage/@7.9029261,98.3465686,15.83z/data=!4m6!3m5!1s0x305031af2ee373f5:0xb44d9e232635a261!8m2!3d7.89823!4d98.355965!16s%2Fg%2F11kr5xp2b2?entry=ttu';

const socialLinks = [
  {
    href: 'https://wa.me/+66641831185',
    label: 'WhatsApp',
    icon: faWhatsapp,
    className: 'text-green-500',
  },
  {
    href: 'https://www.instagram.com/amonthanat_massage',
    label: 'Instagram',
    icon: faInstagram,
    className: 'text-pink-500',
  },
  {
    href: googleMapsUrl,
    label: 'Google Reviews',
    icon: faGoogle,
    className: 'text-amber-600',
  },
];

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleCloseMenu = () => {
      setIsOpen(false);
    };

    const handleOutsideClick = (event) => {
      if (!event.target.closest('header')) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleCloseMenu, { passive: true });
    window.addEventListener('touchmove', handleCloseMenu, { passive: true });
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleCloseMenu);
      window.removeEventListener('touchmove', handleCloseMenu);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4 md:px-6">
      <nav
        className={`mx-auto flex max-w-7xl items-center justify-between rounded-full border px-4 py-3 transition-all duration-300 md:px-6 ${
          isScrolled
            ? 'border-slate-200 bg-white/96 shadow-xl shadow-slate-900/10 backdrop-blur'
            : 'border-white/50 bg-white/88 shadow-lg shadow-slate-900/5 backdrop-blur'
        }`}
      >
        <Link to="/" className="flex items-center gap-3 text-slate-900">
          <img
            src={logoImage}
            alt="Amonthanat Massage logo"
            className="h-11 w-11 rounded-full border border-slate-200 object-cover md:h-12 md:w-12"
          />
          <div className="leading-tight">
            <p className="font-cyclin text-lg font-bold tracking-wide md:text-xl">
              Amonthanat Massage
            </p>
            <p className="hidden text-xs font-medium uppercase tracking-[0.28em] text-slate-500 sm:block">
              Traditional Thai Massage
            </p>
          </div>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          <NavLinks />
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="tel:+66641831185"
            className="rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
          >
            +66 64 183 1185
          </a>
          <div className="flex items-center gap-2">
            {socialLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                aria-label={item.label}
                className={`flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50 ${item.className}`}
              >
                <FontAwesomeIcon icon={item.icon} />
              </a>
            ))}
          </div>

          <a
            href="https://wa.me/+66641831185"
            target="_blank"
            rel="noreferrer"
            className="rounded-full bg-slate-900 px-5 py-3 font-cyclin text-base font-bold text-white transition hover:bg-slate-700"
          >
            Book Now
          </a>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <a
            href="tel:+66641831185"
            className="hidden rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 sm:inline-flex"
          >
            Call
          </a>
          <a
            href="https://wa.me/+66641831185"
            target="_blank"
            rel="noreferrer"
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
          >
            Book
          </a>
          <button
            type="button"
            onClick={() => setIsOpen((open) => !open)}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isOpen ? (
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              ) : (
                <path
                  d="M4 7H20M4 12H20M4 17H20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {isOpen && (
        <div className="px-1 pt-3 lg:hidden">
          <div
            id="mobile-navigation"
            className="mx-auto max-w-7xl rounded-[2rem] border border-slate-200 bg-white p-6 shadow-2xl shadow-slate-900/10"
          >
            <div className="flex flex-col gap-5">
              <a
                href="tel:+66641831185"
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-lg font-semibold text-slate-900"
              >
                Call: +66 64 183 1185
              </a>
              <NavLinks mobile onNavigate={closeMenu} />
              <div className="flex items-center justify-center gap-3 border-t border-slate-100 pt-4">
                {socialLinks.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={item.label}
                    className={`flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-slate-50 ${item.className}`}
                  >
                    <FontAwesomeIcon icon={item.icon} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default NavBar;
